import { z } from "zod";
import { IUserStatus } from "../../types";
import { YayWebSocket } from "../web-socket/webSocket";
import NebualApi, { CACHE_PRESENCE_ADDR, NebulaLoginDetails, nebulaServerOrDefault } from "./neulaApi";
import { SocketStatus } from ".";
import { SocketBackoff } from "../SocketBackoff";
import { EVENT_SOCKET_STATUS } from "../store";

const NEBULA_PRESENCE_WS_URL = nebulaServerOrDefault(CACHE_PRESENCE_ADDR).replace('http', 'ws')

interface NebulaSocket {
    loginDetails: NebulaLoginDetails,
    ws: YayWebSocket,
}

const AvailabilityTypeSchema = z.enum([
    'open',
    'available',
    'unavailable',
    'early',
    'proceeding',
    'confirmed',
    'dnd',
    'closed'
]);

const NebulaPresenceSchema = z.object({
    app: z.object({
        dnd: z.boolean(),
        status: AvailabilityTypeSchema.or(z.string()).nullish(),
    }).nullish(),
    aux_code: z.object({
        code: z.string(),
        set_by: z.string(),
        time: z.string(),
    }).nullish(),
    details: z.any().nullable(),
    global_dnd: z.boolean(),
    queue_available: z.boolean(),
    queue_login: z.boolean(),
    registrations: z.array(z.string()),
    sipuser: z.string(),
    status: AvailabilityTypeSchema.or(z.string()),
    time: z.string(),
});

const PresenceStatusSnapshot = z.object({
    method: z.string(),
    params: NebulaPresenceSchema
})

const NebualSubscribeResponse = z.object({
    statuses: z.record(z.string(), NebulaPresenceSchema)
})

export type NebulaPresence = z.infer<typeof NebulaPresenceSchema>;

let socket: NebulaSocket | null = null;
let lock = false;

const socketBackoff = new SocketBackoff();

function NebulaPresenceToGalaxy(presence: NebulaPresence): IUserStatus {
    return {
        chat: {
            connected: false,
            last_seen: 0,
            status: undefined,
        },
        ...presence,
    }
}

export function checkPresenceConnectedHarsh(): boolean {
    if (!socket) {
        return false;
    }

    return socket.ws.check_is_connected();
}

export const checkPresenceSocket = (): SocketStatus => {
    if (!socket) return 'ok';

    if (!socket.ws.check_is_connected()) return 'no_connection';

    // if (WEBSOCKET.get_auth_status() !== 1) return "no_auth";

    return 'ok';
}

export const resetPresenceBackoff = () => {
    socketBackoff.reset();
};

export const recoverPresenceSocket = () => {
    if (!socket) return;
    socketBackoff.reset();
    socket.ws.force_connect();
};

function createSocket(
    loginDetails: NebulaLoginDetails,
    updateUserPresence: (presence: IUserStatus) => void,
): Promise<void> {
    const ws = new YayWebSocket(`${NEBULA_PRESENCE_WS_URL}/v1/presence/ws?meta=presence&token=${loginDetails.token}`, [])
    ws.set_auth_status(1);

    ws.onConnect = () => {
        console.debug("Connected to Nebula Presence");
        window.dispatchEvent(new Event(EVENT_SOCKET_STATUS));

        ws.subscribe('presence_user_status', (data: unknown) => {
            const validated = PresenceStatusSnapshot.safeParse(data);

            if (validated.success) {
                updateUserPresence(NebulaPresenceToGalaxy(validated.data.params));
            } else {
                console.error("Invalid presence update", validated.error);
            }
        })

        setTimeout(() => {
            ws.ping();
        }, 1_000);
    }

    /*
     * This close happens when ping fails / need to reset auth
     *
     * Closing from losing internet connection is handled in
     *   the offline / online handler
     * */
    ws.onClose = () => {
        window.dispatchEvent(new Event(EVENT_SOCKET_STATUS));

        if (!window.navigator.onLine) {
            return;
        }

        const backoffMs = socketBackoff.getNextBackoff();

        setTimeout(() => {
            if (!socket || !socket.ws) return;
            connectPresneceSocket().then(() => console.log("Reconnected"));
        }, backoffMs);
    };

    socket = {
        loginDetails,
        ws,
    }

    return connectPresneceSocket();
}

export function connectPresneceSocket() {
    return new Promise<void>((resolve, reject) => {
        if (!socket) {
            reject(new Error("Socket not initialized"));
            return;
        }

        socket.ws.connect();

        setTimeout(() => {
            if (!socket) {
                reject(new Error("Socket Dropped"));
                return;
            }

            if (socket.ws.check_is_connected()) {
                resolve()
            } else {
                const backoffMs = socketBackoff.getNextBackoff();

                setTimeout(() => {
                    connectPresneceSocket()
                        .then(() => {
                            resolve()
                        })
                        .catch(e => {
                            reject(e);
                        })
                }, backoffMs);
            }

        }, 1_000)
    });
}

export function initialisePresenceSocket(sipName: string, sipPassword: string, updateUserPresence: (presence: IUserStatus) => void) {
    return new Promise<void>((resolve, reject) => {
        if (socket && socket.loginDetails.name === sipName) {
            connectPresneceSocket().then(() => { /* */ });
            resolve();
            return
        }

        NebualApi.login(sipName, sipPassword)
            .then(loginDetails => {
                if (lock) {
                    return;
                }
                lock = true;

                createSocket(loginDetails, updateUserPresence)
                    .then(() => resolve())
                    .catch(e => reject(e))
            })
            .catch(e => reject(e))
    })
}

export function subscribeUserPresence(sipNames: string[], parkingSlots: number[] | undefined) {
    return new Promise<Record<string, IUserStatus>>((resolve, reject) => {
        if (!socket) {
            reject(new Error("Socket doesn't exist"));
            return;
        }

        if (!socket.ws.check_is_connected()) {
            reject(new Error("Socket Not Connected"))
        }

        let users = sipNames.map(u => u.toLowerCase());

        if (parkingSlots) {
            users = users.concat(parkingSlots.map(slot => `${slot}@parking`))
        }

        socket.ws.call(
            'subscribe_user_status',
            {
                users
            },
            {
                onOne: true
                // allowTime: 20000
            }
        )
            .then(res => {
                const parsed = NebualSubscribeResponse.safeParse(res.result);

                if (!parsed.success) {
                    reject(new Error("Failed to parse", { cause: parsed.error }));
                    return
                }

                const final: Record<string, IUserStatus> = {};

                Object.entries(parsed.data.statuses).forEach(([user, status]) => {
                    // TODO - Remove IUserStatus
                    // When fully migrating, use `NebulaPresenceSchema` for presence type gen
                    final[user] = NebulaPresenceToGalaxy(status);
                })

                resolve(final);
            })
            .catch(e => {
                socketBackoff.getNextBackoff();
                console.error("Failed", typeof e, e);
                reject(e)
            })
    })
}
